<template>
  <div>
    <div class="file-upload-component">
      <FileUpload
        :multiple="true"
        :file-limit="fileLimit"
        :accept="accept"
        :max-file-size="maxFileSizeMb * 1024 * 1024"
        :show-upload-button="false"
        :show-cancel-button="false"
        :invalid="invalid"
        invalid-file-size-message="ファイルのサイズが大きすぎます"
        :invalid-file-limit-message="`添付できるファイルは${fileLimit}件のみです`"
        invalid-file-type-message="ファイルの形式が正しくありません。添付可能なファイル形式はPDF,Word,画像のみです。"
        choose-label="ファイル"
        :choose-button-props="{
          text: true,
        }"
        @select="selectFile"
        @remove="removeFile"
      >
        <template #empty>
          <p v-if="maxFileSizeMb < 1000" class="" style="font-size: 0.75rem">
            ファイル1つの最大サイズは{{
              maxFileSizeMb
            }}MBです。添付可能なファイルの最大数はあと{{ fileLimit }}件です。
          </p>
          <p v-else style="font-size: 0.75rem">
            ファイル1つの最大サイズは{{
              maxFileSizeMb / 1000
            }}GBです。添付可能なファイルの最大数はあと{{ fileLimit }}件です。
          </p>
          <p style="font-size: 0.75rem">
            ファイルをドラッグ＆ドロップするか、
            <strong>+ファイル</strong>ボタンをクリックしてください。
          </p>
        </template>
        <template
          #content="{
            files,
            uploadedFiles,
            removeUploadedFileCallback,
            removeFileCallback,
          }"
        >
          <div class="d-flex flex-column pt-4">
            <div v-if="files.length > 0">
              <div class="d-flex flex-wrap">
                <div
                  v-for="(file, fileIndex) of files"
                  :key="file.name + file.type + file.size"
                  class="p-8 rounded-border d-flex flex-column border border-surface align-items-center justify-content-center p-4"
                >
                  <div class="position-relative">
                    <div
                      class="position-absolute"
                      style="top: 0; left: 50px"
                    ></div>
                  </div>

                  <div v-if="isImage(file.name)">
                    <div class="d-flex">
                      <img
                        role="presentation"
                        :alt="file.name"
                        :src="file.objectURL"
                        width="75"
                      />
                      <div>
                        <Button
                          icon="pi pi-times"
                          @click="
                            onRemoveTemplatingFile(
                              file,
                              removeFileCallback,
                              fileIndex
                            )
                          "
                          outlined
                          rounded
                          severity="danger"
                        />
                      </div>
                    </div>
                  </div>
                  <Inplace closable>
                    <template #display>
                      <span
                        class="font-semibold text-ellipsis max-w-60 whitespace-nowrap overflow-hidden"
                        >{{ extractFilename(fileNames[fileIndex]) }}</span
                      >
                    </template>
                    <template #content>
                      <span class="inline-flex items-center p-2">
                        <InputText
                          :model-value="extractFilename(fileNames[fileIndex])"
                          autofocus
                          @update:model-value="
                            updateFileName(
                              `${$event}.${extractExtension(
                                fileNames[fileIndex]
                              )}`,
                              fileIndex
                            )
                          "
                        />
                      </span>
                    </template>
                  </Inplace>
                </div>
              </div>
            </div>
          </div>
        </template>
      </FileUpload>
      <span
        v-if="newAttachedCount > fileLimit && invalid"
        class="text-danger mt-4"
      >
        添付可能な数を越えています
      </span>
      <span
        v-else-if="fileMin && fileMin > newAttachedCount"
        class="text-danger mt-4"
      >
        ファイルを添付して下さい。
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { extractExtension, extractFilename, isImage } from "/@/modules/file";
import FileUpload from "primevue/fileupload";
import Button from "primevue/button";
import Inplace from "primevue/inplace";
import InputText from "primevue/inputtext";

const modelValue = defineModel<string[] | File[]>();

const props = withDefaults(
  defineProps<{
    fileLimit?: number;
    fileMin?: number;
    invalid?: boolean;
    accept?: string;
    maxFileSizeMb?: number;
    base64?: boolean;
  }>(),
  {
    fileLimit: 1,
    accept: ".pdf,.docx,.doc,image/*",
    maxFileSizeMb: 10,
    base64: true,
  }
);

const currentCount = computed(() => modelValue.value?.length || 0);

function embedFileNameToDataUrl(dataurl: string, filename: string) {
  return dataurl.replace(/;base64,/, `;name=${filename};base64,`);
}

function replaceFileNameInDateUrl(dataurl: string, filename: string) {
  return dataurl.replace(/;name=.*?;/, `;name=${filename};`);
}

const files = ref<File[]>([]);

function updateFileName(filename: string, idx: number) {
  const newFiles = [...files.value];
  // const myNewFile = new File([myFile], 'new_name.png', {type: myFile.type});
  newFiles[idx] = new File([newFiles[idx]], filename, {
    type: newFiles[idx].type,
  });

  files.value = newFiles;
}

watch(files, async (newFiles) => {
  if (!props.base64) {
    modelValue.value = [...newFiles];
    return;
  }

  const results: string[] = await Promise.all(
    newFiles.map(async (file: any) => {
      const reader = new FileReader();

      let blob = new Blob([file], { type: file.type });

      await reader.readAsDataURL(blob);

      return new Promise(
        (resolve) =>
          (reader.onload = () =>
            resolve(embedFileNameToDataUrl(reader.result as string, file.name)))
      );
    })
  );

  modelValue.value = results;
});

const fileNames = computed(() => {
  return files.value.map((file) => file.name);
});

async function selectFile(e: any) {
  files.value = [...e.files];
}

async function removeFile(e: any) {
  const files = e.files;

  if (!props.base64) {
    modelValue.value = [...files];
    return;
  }

  const results: string[] = await Promise.all(
    files.map(async (file: any) => {
      const reader = new FileReader();

      let blob = new Blob([file], { type: file.type });

      await reader.readAsDataURL(blob);

      return new Promise(
        (resolve) => (reader.onload = () => resolve(reader.result))
      );
    })
  );
  modelValue.value = [...results];
}

const newAttachedCount = computed(() => {
  return modelValue.value?.length || 0;
});

function onRemoveTemplatingFile(
  file: any,
  removeFileCallback: (index: number) => void,
  index: number
) {
  removeFileCallback(index);
}
</script>

<style lang="scss">
.file-upload-component {
  .p-fileupload-file-thumbnail,
  .p-fileupload-file-badge {
    display: none;
  }
}

.file-upload-component:has(.p-fileupload-buttonbar .p-invalid) {
  border-color: red;
}
</style>
